(function () {
  const Catalog = window.ImedRepairCatalog;
  if (!Catalog) {
    console.error('imed-repair-catalog.js must load before imed-konfigurator.js');
    return;
  }

  const {
    buildCatalog,
    fetchCollection,
    filterModelNames,
    getUniqueModelNames,
    formatMoney,
    VariantPickerUI,
    hideEmptyCategoryButtons,
    prefetchCollections,
    isEscooterProduct,
  } = Catalog;

  function readConfig(root) {
    const defaults = {
      durationText: 'Reparaturdauer: je nach Aufwand',
      buttonLabel: 'JETZT KAUFEN',
      appointmentButtonLabel: 'Termin vor Ort vereinbaren',
      appointmentUrl: 'https://calendly.com/webshop-imed-mobile/30min',
      prefetchCollections: [],
      placeholderIcons: {},
    };

    try {
      const configEl = root.querySelector('[data-konfigurator-config]');
      if (!configEl) return defaults;
      return { ...defaults, ...JSON.parse(configEl.textContent) };
    } catch (error) {
      console.error('Konfigurator-Konfiguration ungültig', error);
      return defaults;
    }
  }

  class ImedKonfigurator {
    constructor(root) {
      this.root = root;
      this.config = readConfig(root);
      this.state = {
        category: null,
        brand: null,
        model: null,
        repair: null,
        product: null,
        variantId: null,
        products: [],
        catalog: null,
        collectionComplete: false,
      };

      this.steps = {
        category: root.querySelector('[data-step="category"]'),
        brand: root.querySelector('[data-step="brand"]'),
        model: root.querySelector('[data-step="model"]'),
      };

      this.loader = root.querySelector('[data-konfig-loader]');
      this.breadcrumb = root.querySelector('[data-konfig-breadcrumb]');
      this.brandGrid = root.querySelector('[data-brand-grid]');
      this.modelSelect = root.querySelector('[data-model-select]');
      this.repairSelect = root.querySelector('[data-repair-select]');
      this.repairField = root.querySelector('[data-repair-field]');
      this.variantSelect = root.querySelector('[data-variant-select]');
      this.variantWrap = root.querySelector('[data-variant-wrap]');
      this.variantTierWrap = root.querySelector('[data-variant-tier]');
      this.variantFlatWrap = root.querySelector('[data-variant-flat]');
      this.variantTypeSelect = root.querySelector('[data-variant-type-select]');
      this.variantColorSelect = root.querySelector('[data-variant-color-select]');
      this.variantColorField = root.querySelector('[data-variant-color-field]');
      this.variantTypeLabel = root.querySelector('[data-variant-type-label]');
      this.variantColorLabel = root.querySelector('[data-variant-color-label]');
      this.variantPicker = new VariantPickerUI({
        elements: {
          wrap: this.variantWrap,
          tierWrap: this.variantTierWrap,
          flatWrap: this.variantFlatWrap,
          typeSelect: this.variantTypeSelect,
          colorSelect: this.variantColorSelect,
          colorField: this.variantColorField,
          flatSelect: this.variantSelect,
          typeLabel: this.variantTypeLabel,
          colorLabel: this.variantColorLabel,
        },
        onVariantChange: (variant) => this.applyVariantSelection(variant),
      });
      this.checkoutEl = root.querySelector('.imed-konfigurator__checkout');

      this.placeholderWrap = root.querySelector('[data-placeholder-wrap]');
      this.resultWrap = root.querySelector('[data-result-wrap]');
      this.priceEl = root.querySelector('[data-total-price]');
      this.metaEl = root.querySelector('[data-time-info]');
      this.buyBtn = root.querySelector('[data-buy-btn]');
      this.buyBtnText = this.buyBtn?.querySelector('.imed-konfigurator__buy-text');
      this.escooterNotice = root.querySelector('[data-escooter-notice]');
      this.productLink = root.querySelector('[data-product-link]');
      this.errorEl = root.querySelector('[data-konfig-error]');

      this.bindEvents();
      // Soft availability refresh; full catalog only loads when a category is chosen.
      this.hideEmptyCategories({ revalidate: true }).catch((error) => {
        console.warn('Kategorien konnten nicht geprüft werden', error);
      });

      if (Array.isArray(this.config.prefetchCollections) && typeof prefetchCollections === 'function') {
        prefetchCollections(this.config.prefetchCollections);
      }
    }

    async hideEmptyCategories(options = {}) {
      if (typeof hideEmptyCategoryButtons !== 'function') return;
      await hideEmptyCategoryButtons(this.root, '[data-category-btn]', options);
    }

    bindEvents() {
      this.root.querySelectorAll('[data-category-btn]').forEach((btn) => {
        btn.addEventListener('click', () => this.selectCategory(btn));
      });

      this.root.querySelector('[data-back-category]')?.addEventListener('click', () => this.goToStep('category'));
      this.root.querySelector('[data-back-brand]')?.addEventListener('click', () => this.goToStep('brand'));

      this.modelSelect?.addEventListener('change', () => this.onModelChange());
      this.repairSelect?.addEventListener('change', () => this.onRepairChange());

      this.buyBtn?.addEventListener('click', (e) => {
        if (!this.state.variantId || this.buyBtn.classList.contains('is-disabled')) {
          e.preventDefault();
        }
      });
    }

    goToStep(step) {
      Object.entries(this.steps).forEach(([name, el]) => {
        el?.classList.toggle('is-active', name === step);
      });

      if (step === 'category') {
        this.resetSelection(false);
      } else if (step === 'brand') {
        this.state.brand = null;
        this.state.model = null;
        this.state.repair = null;
        this.modelSelect.innerHTML = '<option value="">Modell wählen...</option>';
        this.repairSelect.innerHTML = '<option value="">Reparatur wählen...</option>';
        this.setRepairFieldVisible(false);
        this.clearResult();
        this.updateBreadcrumb();
      }
    }

    setLoading(active) {
      this.loader?.classList.toggle('is-visible', active);
    }

    handleCatalogUpdate(handle, complete) {
      if (this.state.category?.handle !== handle) return;
      if (!complete) {
        this.setLoading(true);
        return;
      }

      this.state.collectionComplete = true;
      this.state.catalog = buildCatalog(this.state.products);

      if (this.steps.brand?.classList.contains('is-active') && !this.state.brand) {
        this.renderBrands();
        this.setLoading(false);
      } else if (this.state.brand && this.steps.model?.classList.contains('is-active')) {
        this.refreshModelsForBrand();
        if (this.state.model) {
          this.onModelChange();
        }
      }
    }

    async selectCategory(btn) {
      const handle = btn.dataset.collectionHandle;
      const label = btn.dataset.categoryLabel;
      if (!handle) return;

      this.root.querySelectorAll('[data-category-btn]').forEach((b) => b.classList.remove('is-active'));
      btn.classList.add('is-active');

      this.state.category = { handle, label };
      this.state.brand = null;
      this.state.model = null;
      this.state.repair = null;
      this.clearResult();
      this.updateBreadcrumb();
      this.goToStep('brand');
      this.setLoading(true);
      this.hideError();
      this.state.collectionComplete = false;
      this.brandGrid.innerHTML = '';

      try {
        await fetchCollection(handle, {
          progressive: true,
          onUpdate: (products, complete) => {
            this.state.products = products;
            this.handleCatalogUpdate(handle, complete);
          },
        });

        if (this.state.collectionComplete) {
          if (!this.state.products.length) {
            this.showError('In dieser Kategorie sind aktuell keine Produkte hinterlegt.');
            this.brandGrid.innerHTML = '';
          }
          return;
        }

        if (!this.state.products.length) {
          this.showError('In dieser Kategorie sind aktuell keine Produkte hinterlegt.');
          this.setLoading(false);
        }
      } catch (err) {
        this.showError('Produkte konnten nicht geladen werden. Bitte erneut versuchen.');
        console.error(err);
        this.setLoading(false);
      }
    }

    renderBrands() {
      if (!this.state.catalog) return;

      const brands = Object.keys(this.state.catalog).sort((a, b) => a.localeCompare(b, 'de'));
      this.brandGrid.innerHTML = '';

      brands.forEach((brand) => {
        const btn = document.createElement('button');
        btn.type = 'button';
        btn.className = 'imed-konfigurator__btn';
        btn.textContent = brand;
        btn.addEventListener('click', () => this.selectBrand(brand));
        this.brandGrid.appendChild(btn);
      });
    }

    setRepairFieldVisible(visible) {
      if (!this.repairField) return;
      this.repairField.hidden = !visible;
      this.repairField.classList.toggle('is-hidden', !visible);
      if (visible) {
        this.repairField.classList.remove('is-revealed');
        void this.repairField.offsetWidth;
        this.repairField.classList.add('is-revealed');
      } else {
        this.repairField.classList.remove('is-revealed');
      }
    }

    refreshModelsForBrand() {
      if (!this.state.brand || !this.state.catalog?.[this.state.brand]) return;

      const currentModel = this.state.model;
      const models = getUniqueModelNames(
        filterModelNames(Object.keys(this.state.catalog[this.state.brand] || {}))
      ).sort((a, b) => a.localeCompare(b, 'de'));

      this.modelSelect.innerHTML = '<option value="">Modell wählen...</option>';
      models.forEach((model) => {
        const opt = document.createElement('option');
        opt.value = model;
        opt.textContent = model;
        this.modelSelect.appendChild(opt);
      });

      if (currentModel && models.includes(currentModel)) {
        this.modelSelect.value = currentModel;
      } else if (currentModel) {
        this.state.model = null;
        this.state.repair = null;
        this.repairSelect.innerHTML = '<option value="">Reparatur wählen...</option>';
        this.setRepairFieldVisible(false);
        this.clearResult();
      }
    }

    selectBrand(brand) {
      this.state.brand = brand;
      this.state.model = null;
      this.state.repair = null;
      this.clearResult();
      this.updateBreadcrumb();

      this.root.querySelectorAll('[data-brand-grid] .imed-konfigurator__btn').forEach((b) => {
        b.classList.toggle('is-active', b.textContent === brand);
      });

      const models = getUniqueModelNames(
        filterModelNames(Object.keys(this.state.catalog[brand] || {}))
      ).sort((a, b) => a.localeCompare(b, 'de'));
      this.modelSelect.innerHTML = '<option value="">Modell wählen...</option>';
      models.forEach((model) => {
        const opt = document.createElement('option');
        opt.value = model;
        opt.textContent = model;
        this.modelSelect.appendChild(opt);
      });

      this.repairSelect.innerHTML = '<option value="">Reparatur wählen...</option>';
      this.setRepairFieldVisible(false);
      this.goToStep('model');
    }

    onModelChange() {
      this.state.model = this.modelSelect.value || null;
      this.state.repair = null;
      this.clearResult();
      this.updateBreadcrumb();
      this.repairSelect.innerHTML = '<option value="">Reparatur wählen...</option>';

      if (!this.state.model) {
        this.setRepairFieldVisible(false);
        return;
      }

      if (!this.state.collectionComplete || !this.state.catalog) {
        this.setRepairFieldVisible(false);
        this.setLoading(true);
        return;
      }

      const repairs = this.state.catalog[this.state.brand][this.state.model] || [];
      const uniqueRepairs = [...new Set(repairs.map((p) => p.parsedRepair))].sort((a, b) =>
        a.localeCompare(b, 'de')
      );

      uniqueRepairs.forEach((repair) => {
        const opt = document.createElement('option');
        opt.value = repair;
        opt.textContent = repair;
        this.repairSelect.appendChild(opt);
      });

      this.setRepairFieldVisible(true);
      this.repairSelect.focus();
    }

    onRepairChange() {
      this.state.repair = this.repairSelect.value || null;
      if (!this.state.repair || !this.state.model) {
        this.clearResult();
        return;
      }

      const matches = (this.state.catalog[this.state.brand][this.state.model] || []).filter(
        (p) => p.parsedRepair === this.state.repair
      );

      if (!matches.length) {
        this.showError('Kein passendes Produkt gefunden.');
        this.clearResult();
        return;
      }

      this.state.product = matches[0];
      this.updateBreadcrumb();
      this.renderProduct(this.state.product);
    }

    updateBuyButton(product) {
      if (!this.buyBtn) return;

      const escooter =
        (typeof isEscooterProduct === 'function' && isEscooterProduct(product)) ||
        (this.state.category?.handle || '').includes('scooter');

      if (escooter) {
        this.buyBtn.href = this.config.appointmentUrl || 'https://calendly.com/webshop-imed-mobile/30min';
        this.buyBtn.setAttribute('target', '_blank');
        this.buyBtn.setAttribute('rel', 'noopener noreferrer');
        if (this.buyBtnText) {
          this.buyBtnText.textContent =
            this.config.appointmentButtonLabel || 'Termin vor Ort vereinbaren';
        }
        this.escooterNotice?.removeAttribute('hidden');
        return;
      }

      this.buyBtn.removeAttribute('target');
      this.buyBtn.removeAttribute('rel');
      if (this.buyBtnText) {
        this.buyBtnText.textContent = this.config.buttonLabel || 'JETZT KAUFEN';
      }
      this.escooterNotice?.setAttribute('hidden', '');
    }

    renderProduct(product) {
      this.hideError();
      this.placeholderWrap?.classList.add('is-hidden');
      this.resultWrap?.classList.add('is-visible');
      this.checkoutEl?.classList.add('has-result');

      const variant = this.variantPicker.render(product);
      if (!variant) {
        this.showError('Dieses Produkt ist derzeit nicht verfügbar.');
        this.clearResult();
        return;
      }

      this.state.variantId = variant.id;
      this.updatePrice(variant);

      this.metaEl.textContent = product.title;
      this.productLink.href = `/products/${product.handle}`;
      this.productLink.textContent = 'Produktdetails ansehen';
      this.productLink.hidden = false;
      this.updateBuyButton(product);
      this.buyBtn.classList.remove('is-disabled');
      this.buyBtn.setAttribute('aria-disabled', 'false');

      if (
        (typeof isEscooterProduct === 'function' && isEscooterProduct(product)) ||
        (this.state.category?.handle || '').includes('scooter')
      ) {
        return;
      }

      this.buyBtn.href = `${window.routes?.cart_add_url || '/cart/add'}?id=${this.state.variantId}`;
    }

    applyVariantSelection(variant) {
      if (!variant) return;
      this.state.variantId = variant.id;
      this.updatePrice(variant);

      if (
        this.state.product &&
        ((typeof isEscooterProduct === 'function' && isEscooterProduct(this.state.product)) ||
          (this.state.category?.handle || '').includes('scooter'))
      ) {
        return;
      }

      this.buyBtn.href = `${window.routes?.cart_add_url || '/cart/add'}?id=${this.state.variantId}`;
    }

    updatePrice(variant) {
      this.priceEl.textContent = formatMoney(variant.price);
      this.priceEl?.classList.remove('is-animate');
      void this.priceEl?.offsetWidth;
      this.priceEl?.classList.add('is-animate');
    }

    clearResult() {
      this.state.product = null;
      this.state.variantId = null;
      this.variantPicker?.reset();
      this.placeholderWrap?.classList.remove('is-hidden');
      this.resultWrap?.classList.remove('is-visible');
      this.checkoutEl?.classList.remove('has-result');
      this.priceEl?.classList.remove('is-animate');
      this.priceEl.textContent = '0,00 €';
      this.metaEl.textContent = this.config.durationText || 'Reparaturdauer: je nach Aufwand';
      this.buyBtn.href = '#';
      this.buyBtn.classList.add('is-disabled');
      this.buyBtn.setAttribute('aria-disabled', 'true');
      this.buyBtn.removeAttribute('target');
      this.buyBtn.removeAttribute('rel');
      if (this.buyBtnText) {
        this.buyBtnText.textContent = this.config.buttonLabel || 'JETZT KAUFEN';
      }
      this.escooterNotice?.setAttribute('hidden', '');
      this.productLink.hidden = true;
    }

    resetSelection(full) {
      if (full) {
        this.state.category = null;
        this.root.querySelectorAll('[data-category-btn]').forEach((b) => b.classList.remove('is-active'));
      }
      this.state.brand = null;
      this.state.model = null;
      this.state.repair = null;
      this.state.products = [];
      this.state.catalog = null;
      this.state.collectionComplete = false;
      this.modelSelect.innerHTML = '<option value="">Modell wählen...</option>';
      this.repairSelect.innerHTML = '<option value="">Reparatur wählen...</option>';
      this.setRepairFieldVisible(false);
      this.brandGrid.innerHTML = '';
      this.clearResult();
      this.updateBreadcrumb();
      this.hideError();
    }

    updateBreadcrumb() {
      const parts = [];
      if (this.state.category) parts.push(this.state.category.label);
      if (this.state.brand) parts.push(this.state.brand);
      if (this.state.model) parts.push(this.state.model);
      if (this.state.repair) parts.push(this.state.repair);

      if (!parts.length) {
        this.breadcrumb.innerHTML = '';
        this.breadcrumb.style.display = 'none';
        return;
      }

      this.breadcrumb.style.display = 'flex';
      this.breadcrumb.innerHTML = parts.map((p) => `<span>${p}</span>`).join('');
    }

    showError(message) {
      if (this.errorEl) {
        this.errorEl.textContent = message;
        this.errorEl.hidden = false;
      }
    }

    hideError() {
      if (this.errorEl) this.errorEl.hidden = true;
    }
  }

  function init() {
    document.querySelectorAll('[data-imed-konfigurator]').forEach((root) => {
      if (root.dataset.initialized) return;

      try {
        root.dataset.initialized = 'true';
        new ImedKonfigurator(root);
      } catch (error) {
        root.dataset.initialized = '';
        console.error('Konfigurator konnte nicht initialisiert werden', error);
      }
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  document.addEventListener('shopify:section:load', init);
})();
